import type {DocumentSide, DocumentTypeVisualKind, DocumentVisual} from './types';

type SideCopy = {
  sideLabel: string;
  sideHint: string;
  sideTitle: string;
  check: string;
  photoTitle: string;
  photoHeading: string;
  photoInstruction: string;
  alt: string;
};

const SIDE_COPY: Record<DocumentSide, SideCopy> = {
  back: {
    sideLabel: 'document_side_back',
    sideHint: 'document_guide_other_side_not_needed',
    sideTitle: 'document_guide_side_title_back',
    check: 'document_guide_check_side_back',
    photoTitle: 'document_guide_photo_title_back',
    photoHeading: 'document_photo_heading_back',
    photoInstruction: 'document_photo_instruction_back',
    alt: 'document_preview_alt_back',
  },
  front: {
    sideLabel: 'document_side_front',
    sideHint: 'document_guide_other_side_not_needed',
    sideTitle: 'document_guide_side_title_front',
    check: 'document_guide_check_side_front',
    photoTitle: 'document_guide_photo_title_front',
    photoHeading: 'document_photo_heading_front',
    photoInstruction: 'document_photo_instruction_front',
    alt: 'document_preview_alt_front',
  },
  data: {
    sideLabel: 'document_side_photo_page',
    sideHint: 'document_guide_photo_page_hint',
    sideTitle: 'document_guide_side_title_passport',
    check: 'document_guide_check_side_passport',
    photoTitle: 'document_guide_photo_title_passport',
    photoHeading: 'document_photo_heading_passport',
    photoInstruction: 'document_photo_instruction_passport',
    alt: 'document_preview_alt_passport',
  },
  lines: {
    sideLabel: 'document_side_with_lines',
    sideHint: 'document_guide_other_side_not_needed',
    sideTitle: 'document_guide_side_title_lines',
    check: 'document_guide_check_side_lines',
    photoTitle: 'document_guide_photo_title_lines',
    photoHeading: 'document_photo_heading_lines',
    photoInstruction: 'document_photo_instruction_lines',
    alt: 'document_preview_alt_lines',
  },
};

const GUIDE_TITLES: Record<DocumentTypeVisualKind, string> = {
  passport: 'document_guide_get_ready_passport',
  id: 'document_guide_get_ready_card',
  driving_license: 'document_guide_get_ready_licence',
};

const DOCUMENT_HINTS: Record<string, string> = {
  AE_ID_GCC: 'document_hint_gcc_id',
  AE_RP: 'document_hint_uae_residence',
  AT_O: 'document_hint_other_document',
  CA_DL: 'document_hint_us_ca_licence',
  CO_FID: 'document_hint_co_residence',
  ES_N: 'document_hint_es_residence',
  ES_X: 'document_hint_es_residence',
  FR_IDENT: 'document_hint_fr_paper_id',
  PT_O: 'document_hint_other_document',
  RO_ID_NEW: 'document_hint_ro_id_new',
  RO_ID_OLD: 'document_hint_ro_id_old',
  US_DL: 'document_hint_us_ca_licence',
};

function getSideCopy(side: DocumentSide): SideCopy {
  return SIDE_COPY[side];
}

function getDocumentGuideTitleKey(visual: DocumentVisual | null): string | null {
  return visual ? GUIDE_TITLES[visual.kind] : null;
}

function getLinesCheckKey(visual: DocumentVisual): string {
  if (visual.kind === 'driving_license') return 'document_guide_check_barcode';
  if (!visual.mrz.some(Boolean)) return 'document_guide_check_corners';
  return 'document_guide_check_lines';
}

function getDocumentHintKey(value?: string | null): string | null {
  return (value && DOCUMENT_HINTS[value]) || null;
}

function getDocumentPhotoTitleKey(visual: DocumentVisual | null): string | null {
  return visual ? SIDE_COPY[visual.side].photoTitle : null;
}

function getDocumentPhotoCopy(
  visual: DocumentVisual | null,
): {heading: string; instruction: string} | null {
  if (!visual) return null;
  const {photoHeading, photoInstruction} = SIDE_COPY[visual.side];
  return {heading: photoHeading, instruction: photoInstruction};
}

function getAutofillStepTitleKey(
  visual: DocumentVisual | null,
  isGuideVisible: boolean,
): string | null {
  return isGuideVisible
    ? getDocumentGuideTitleKey(visual)
    : getDocumentPhotoTitleKey(visual);
}

export {
  getAutofillStepTitleKey,
  getDocumentPhotoCopy,
  getDocumentGuideTitleKey,
  getDocumentHintKey,
  getDocumentPhotoTitleKey,
  getLinesCheckKey,
  getSideCopy,
};
